import React from 'react';
import Container from '@amzn/awsui-components-console/container';
import Link from '@amzn/awsui-components-console/link';
import { formatEpochMillisDate } from 'src/features/namespace/utils';
import { useTranslation } from 'src/i18n/shim/useTranslation';
import styles from './NamespaceMetadataCard.module.scss';
import ContentLayout from '@amzn/awsui-components-console/content-layout';

export interface NamespaceMetadataCardProps {
  readonly cxFamily?: string | null;
  readonly bindleLabel?: string | null;
  readonly bindleHref?: string;
  readonly ownerHref?: string;
  readonly createdAt?: number | null;
  readonly createdBy?: string | null;
}

/**
 * Stacked metadata card displaying namespace metadata in a bordered container
 * with divider-separated label/value rows using semantic description-list markup.
 */
const NamespaceMetadataCard: React.FC<NamespaceMetadataCardProps> = ({
  cxFamily,
  bindleLabel,
  bindleHref,
  ownerHref,
  createdAt,
  createdBy,
}) => {
  const { t, locale } = useTranslation();

  const formatDateWithAuthor = (
    epochMillis?: number | null,
    author?: string | null,
  ): string => {
    const date = formatEpochMillisDate(t, locale, epochMillis);
    if (author) {
      return t('namespace-metadata-date-by-author', { date, author });
    }
    return date;
  };

  return (
    <ContentLayout maxContentWidth={390}>
      <Container>
        <dl className={styles.list}>
          {cxFamily?.trim() && (
            <div className={styles.row}>
              <dt className={styles.label}>
                {t('namespace-metadata-cx-family')}
              </dt>
              <dd className={styles.value}>{cxFamily}</dd>
            </div>
          )}
          <div className={styles.row}>
            <dt className={styles.label}>
              {t('namespace-metadata-linked-bindle')}
            </dt>
            <dd className={styles.value}>
              <Link external href={bindleHref} />
            </dd>
          </div>
          <div className={styles.row}>
            <dt className={styles.label}>
              {t('namespace-metadata-created-at')}
            </dt>
            <dd className={styles.value}>
              {formatDateWithAuthor(createdAt, createdBy)}
            </dd>
          </div>
          <div className={styles.row}>
            <dt className={styles.label}>{t('namespace-metadata-owner')}</dt>
            <dd className={styles.value}>
              <Link external href={ownerHref} />
            </dd>
          </div>
        </dl>
      </Container>
    </ContentLayout>
  );
};

export default NamespaceMetadataCard;
